import { Table, TableBody, TableCell, TableContainer, TableHead } from "@mui/material";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { ControlGroup } from "../views/ControlGroup";
import { TableBodyRow, TableHeaderRow } from "../../../../shared/views";
import { POSITIONS_TABLE_ROW_PADDING_Y } from "../../../../shared/constants/table.constants";
import { numberFormatters } from "../../../../shared/utils/numberFormatters";
import { ContractDetailsState } from "./ContractDetails";
import { PricingDetailsState } from "./PricingParameters";
import { oderManagerUtils } from "../utils/OrderManagerUtils";
import { useAppContext } from "../../../../../shared/contexts/app-context/components/AppContext";

type OptionPrice = {
    shift: number;
    dateTime: Dayjs;
    price: number;
    deltaPrice: number;
    aggregatedDelta: number;
}

type OptionPriceChangingInfoProps = {
    contractDetails: ContractDetailsState;
    pricingDetails: PricingDetailsState;
}

export function OptionDeltaTrend({
    contractDetails,
    pricingDetails,
}: OptionPriceChangingInfoProps){
    const {
        marketDataDto,
    } = useAppContext();
    
    const [selectedItem, setSelectedItem] = useState<OptionPrice | null>(null);
    
    const data: OptionPrice[] = [];
    const maxShiftNumber = 24;
    
    for (let x = 0; x <= maxShiftNumber; x++) {
        const targetDateTime = pricingDetails.dateForPricing.add(x, 'hour');

        const { price, delta, gamma, vega, theta } = oderManagerUtils.getOptionParameters(
            marketDataDto!.OptionInfos, contractDetails.assetType, contractDetails.strike!,
            contractDetails.expiration, pricingDetails.impliedVolatilityForPricing,
            pricingDetails.underlyingPriceForPricing, targetDateTime.toDate());
        
        data.push({
            shift: x,
            dateTime: targetDateTime,
            price: price,
            deltaPrice: x === 0
                ? 0
                : price - data[x - 1].price,
            aggregatedDelta: x === 0
                ? 0
                : price - data[0].price
        });
    }

    const handleOnItemClick = (position: OptionPrice) => {
        setSelectedItem(position);
    };
    
    return(
        <ControlGroup title="Option delta by hour">
            <TableContainer>
                <Table
                    size="small"
                    stickyHeader
                    sx={{padding: '0px', margin: '0px', verticalAlign: 'text-top'}}

                >
                    <TableHead>
                        <TableHeaderRow>
                            <TableCell>Shift</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Δ</TableCell>
                            <TableCell>∑Δ</TableCell>
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        {data.map((optionPrice) => (
                            <TableBodyRow
                                onClick={() => handleOnItemClick(optionPrice)}
                                paddingY={POSITIONS_TABLE_ROW_PADDING_Y}
                                style={{
                                    ...(selectedItem && selectedItem.shift === optionPrice.shift
                                        ? { backgroundColor: 'rgba(255,224,122,0.5)' }
                                        : {}),
                                    cursor: "pointer"
                                }}
                            >
                                <TableCell align="right">
                                    {optionPrice.shift}
                                </TableCell>
                                <TableCell>
                                    {optionPrice.dateTime.format("DD.MMM HH:mm")}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: '40px'}}>
                                    {numberFormatters.format0(optionPrice.price)}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: '40px'}}>
                                    {numberFormatters.format0(optionPrice.deltaPrice)}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: '40px'}}>
                                    {numberFormatters.format0(optionPrice.aggregatedDelta)}
                                </TableCell>
                            </TableBodyRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </ControlGroup>
    )
}